import { Link, Outlet } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Plus, Upload } from "lucide-react"
import { useCustomersQuery } from "modules/customers/hooks/useCustomersQuery"
import "modules/customers/lang"
import { Loading } from "modules/shared/components/Loading"
import { Pagination } from "modules/shared/components/Pagination"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { usePagination } from "modules/shared/hooks/usePagination"
import { useSortBy } from "modules/shared/hooks/useSortBy"
import { useTranslation } from "react-i18next"
import { Route as CustomersListRoute } from "routes/_base/_auth/customers/list/route"
import { customersSortFields } from "schema"
import { CustomersFilter } from "./CustomersFilter"
import { CustomersTable } from "./CustomersTable"
import { RunningImports } from "./RunningImports"

const ITEMS_PER_PAGE = 50

export function CustomersList() {
  const { t } = useTranslation()
  const search = CustomersListRoute.useSearch()
  const searchQuery = search.query?.trim()
  const isSearchTooShort = Boolean(
    searchQuery && searchQuery.replace(/^@/, "").length < 3 && !/^#[1-9]\d*$/.test(searchQuery),
  )

  const { page, totalPages, setPage, setCount, count } = usePagination({
    search,
    fullPath: CustomersListRoute.fullPath,
    total: ITEMS_PER_PAGE,
  })

  const { onSort, sortBy } = useSortBy<typeof customersSortFields>({
    search,
    fullPath: CustomersListRoute.fullPath,
    fields: customersSortFields,
  })

  const { data: customers, isPending } = useCustomersQuery({
    enabled: !isSearchTooShort,
    query: {
      query: searchQuery,
      page,
      total: ITEMS_PER_PAGE,
      sortBy,
      filterBy: {
        ...search.filterBy,
        isFake: false,
      },
    },
    onSuccess: (data) => {
      setCount(data.pageInfo.count)
    },
  })

  const isPendingTable = useDebouncedValue({
    value: isPending,
  })

  return (
    <div className="flex flex-col gap-8 w-full h-full">
      <div className="flex flex-col gap-4">
        <div className="flex flex-row gap-2 w-full items-start">
          <CustomersFilter />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-10 w-10 shrink-0"
                asChild
                aria-label={t("customers.importCustomers")}
              >
                <Link to="/customers/list/import" search={search}>
                  <Upload className="size-4" strokeWidth={2} />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("customers.importCustomers")}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="default"
                size="icon"
                className="h-10 w-10 shrink-0"
                asChild
                aria-label={t("customers.createCustomer")}
              >
                <Link to="/customers/list/create" search={search}>
                  <Plus className="size-4" strokeWidth={2} />
                </Link>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("customers.createCustomer")}</TooltipContent>
          </Tooltip>
        </div>
        <RunningImports />
        <div className="flex flex-col gap-4">
          {isSearchTooShort && (
            <p className="text-sm text-muted-foreground" role="status">
              {t("customers.table.searchMinimum")}
            </p>
          )}
          {!isSearchTooShort && (isPending || customers === undefined) && <Loading />}
          {!isSearchTooShort && customers !== undefined && (
            <>
              <CustomersTable
                customers={customers}
                isPending={isPendingTable}
                fields={[...customersSortFields]}
                onSort={onSort}
                search={search}
              />
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
                count={count}
              />
            </>
          )}
        </div>
      </div>
      <Outlet />
    </div>
  )
}
